import React, { useEffect, useRef, useState } from 'react'
import { ActionMenu } from './ActionMenu'
import ThreeDotsMenuVertical from '../../Assets/Images/icons/threeDotsMenuVertical'
import { DESIGN_STATUS } from './hooks/useDesignManagement'
import { Figma } from 'Assets/Images/logos'

export const DesignRow = ({
  id,
  imageSrc,
  designName,
  designSource,
  format,
  designer,
  clientId,
  dateCreated,
  status,
  actions,
  onRowClick,
  isDuplicating = false,
  isAdmin = false,
  clientsMap = {},
}) => {
  const [isActionMenuOpen, setIsActionMenuOpen] = useState(false)
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [isEditingName, setIsEditingName] = useState(false)
  const [editedName, setEditedName] = useState(designName)
  const clickOutsideRef = useRef(null)
  const nameInputRef = useRef(null)

  const isFigma = designSource === 'figma'

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        clickOutsideRef.current &&
        !clickOutsideRef.current.contains(event.target)
      ) {
        setIsActionMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [clickOutsideRef, setIsActionMenuOpen])

  const handleImageLoad = () => {
    setImageLoaded(true)
    setImageError(false)
  }

  const handleImageError = () => {
    setImageError(true)
    setImageLoaded(false)
  }
  const renderDate = dateCreated => {
    let locale = 'en-US'
    try {
      locale = navigator.languages[0] || 'en-US'
    } catch (error) {
      console.warn('Error getting locale:', error)
    }

    const formattedDate = new Date(dateCreated).toLocaleDateString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    })
    return formattedDate
  }

  const handleRowClick = e => {
    // Don't trigger row click if clicking on action menu, name field in edit mode, or the three dots
    if (
      e.target.closest('.action-menu-trigger') ||
      e.target.closest('.name-cell') ||
      isEditingName
    ) {
      return
    }
    if (onRowClick) {
      onRowClick(id)
    }
  }

  const handleNameDoubleClick = e => {
    e.stopPropagation()
    setIsEditingName(true)
    setEditedName(designName)
  }

  const handleNameSave = () => {
    if (editedName.trim() && editedName !== designName) {
      // Find the update action and call it
      const updateAction = actions.find(
        a => a.label === 'Update' || a.label === 'Rename',
      )
      if (updateAction) {
        updateAction.action(id, { filename: editedName })
      }
    }
    setIsEditingName(false)
  }

  const handleNameKeyDown = e => {
    if (e.key === 'Enter') {
      handleNameSave()
    } else if (e.key === 'Escape') {
      setIsEditingName(false)
      setEditedName(designName)
    }
  }

  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus()
      nameInputRef.current.select()
    }
  }, [isEditingName])

  return (
    <article
      style={{
        position: 'relative',
        display: 'flex',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        gap: '40px',
        alignItems: 'center',
        paddingLeft: '12px',
        padding: '12px',
        paddingRight: '32px',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #E2E2FC',
        cursor: 'pointer',
        opacity: isDuplicating ? 0.6 : 1,
      }}
      onClick={handleRowClick}
    >
      {isDuplicating && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.75)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              backgroundColor: '#dbeafe',
              padding: '8px 16px',
              borderRadius: '8px',
            }}
          >
            <svg
              style={{
                animation: 'spin 1s linear infinite',
                height: '20px',
                width: '20px',
                color: '#2563eb',
              }}
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                style={{ opacity: 0.25 }}
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                style={{ opacity: 0.75 }}
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
            <span
              style={{ fontSize: '14px', fontWeight: 500, color: '#2563eb' }}
            >
              Duplicating design...
            </span>
          </div>
        </div>
      )}
      <div
        style={{
          display: 'flex',
          gap: '1px',
          alignItems: 'center',
          alignSelf: 'stretch',
          padding: '1px',
          margin: 'auto 0',
          backgroundColor: 'rgb(249, 250, 251)',
          borderRadius: '2px',
          minHeight: '94px',
          width: '170px',
          border: '1px solid #E2E2FC',
        }}
      >
        {imageSrc && !imageError ? (
          <img
            src={imageSrc}
            alt={designName}
            style={{
              background: 'white',
              objectFit: 'contain',
              flex: 1,
              flexShrink: 1,
              alignSelf: 'stretch',
              margin: 'auto 0',
              aspectRatio: 1.77,
              flexBasis: 0,
              width: '166px',
              height: '95px',
              opacity: imageLoaded ? 1 : 0,
            }}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1,
              flexShrink: 1,
              alignSelf: 'stretch',
              margin: 'auto 0',
              aspectRatio: 1.77,
              flexBasis: 0,
              width: '170px',
              backgroundColor: '#f9fafb',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ marginBottom: '8px', color: '#9ca3af' }}
            >
              <path
                d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z"
                fill="currentColor"
              />
            </svg>
            <p
              style={{
                fontSize: '12px',
                lineHeight: '1',
                textAlign: 'center',
                color: '#6b7280',
                margin: 0,
              }}
            >
              Preview not available
            </p>
          </div>
        )}
      </div>
      <span
        style={{
          position: 'relative',
          alignSelf: 'stretch',
          margin: 'auto 0',
          fontSize: '14px',
          lineHeight: '1',
          color: '#0f172a',
          width: '200px',
          padding: '4px 8px',
          borderRadius: '4px',
          cursor: 'pointer',
          transition: 'all 0.2s ease',
        }}
        onDoubleClick={handleNameDoubleClick}
      >
        {isEditingName ? (
          <input
            ref={nameInputRef}
            type="text"
            value={editedName}
            onChange={e => setEditedName(e.target.value)}
            onBlur={handleNameSave}
            onKeyDown={handleNameKeyDown}
            style={{
              width: '100%',
              padding: '4px 8px',
              fontSize: '14px',
              border: '1px solid #d1d5db',
              borderRadius: '4px',
              outline: 'none',
            }}
            onClick={e => e.stopPropagation()}
          />
        ) : (
          <React.Fragment>
            <div
              style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}
            >
              {designName}
              {isFigma && (
                <span
                  style={{
                    display: 'flex',
                    gap: '4px',
                    fontWeight: 400,
                    fontSize: '12px',
                    lineHeight: '16px',
                    letterSpacing: '0px',
                    color: '#626379',
                  }}
                >
                  <Figma />
                  imported from Figma
                </span>
              )}
            </div>
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '8px',
                padding: '8px 12px',
                fontSize: '12px',
                color: '#ffffff',
                backgroundColor: '#111827',
                borderRadius: '8px',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
                opacity: 0,
                pointerEvents: 'none',
                transition: 'opacity 0.15s ease',
                whiteSpace: 'nowrap',
                zIndex: 50,
              }}
            >
              Double-click to edit name
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '4px solid transparent',
                  borderRight: '4px solid transparent',
                  borderTop: '4px solid #111827',
                }}
              />
            </div>
          </React.Fragment>
        )}
      </span>
      <span
        style={{
          alignSelf: 'stretch',
          margin: 'auto 0',
          fontSize: '14px',
          lineHeight: '1',
          color: '#0f172a',
          width: '20px',
        }}
      >
        {format}
      </span>
      <span
        style={{
          alignSelf: 'stretch',
          margin: 'auto 0',
          fontSize: '14px',
          lineHeight: '1',
          color: '#0f172a',
          width: '50px',
        }}
      >
        {designer}
      </span>
      {isAdmin && (
        <span
          style={{
            alignSelf: 'stretch',
            margin: 'auto 0',
            fontSize: '14px',
            lineHeight: '1',
            color: '#0f172a',
            width: '100px',
          }}
        >
          {clientsMap[clientId] || clientId || '-'}
        </span>
      )}
      <span
        style={{
          alignSelf: 'stretch',
          margin: 'auto 0',
          fontSize: '14px',
          lineHeight: '1.4',
          color: '#0f172a',
          width: '100px',
        }}
      >
        {dateCreated ? renderDate(dateCreated) : '-'}
      </span>
      <span
        style={{
          alignSelf: 'stretch',
          margin: 'auto 0',
          fontSize: '14px',
          lineHeight: '1',
          color: '#0f172a',
          width: '80px',
        }}
      >
        {status === DESIGN_STATUS.ACTIVE ? 'Active' : 'Archived'}
      </span>
      <span
        style={{
          alignSelf: 'stretch',
          margin: 'auto 0',
          fontSize: '14px',
          lineHeight: '1',
          color: '#0f172a',
          width: '20px',
          cursor: 'pointer',
          position: 'relative',
        }}
        onClick={e => {
          e.stopPropagation()
          setIsActionMenuOpen(!isActionMenuOpen)
        }}
      >
        <ThreeDotsMenuVertical />
        {isActionMenuOpen ? (
          <div
            ref={clickOutsideRef}
            style={{
              position: 'absolute',
              top: '100%',
              marginTop: '8px',
              right: '0',
              zIndex: 10,
            }}
            onClick={e => e.stopPropagation()}
          >
            <ActionMenu
              id={id}
              actions={actions.filter(action =>
                action.isVisible({ status, designSource }),
              )}
              onActionClick={() => setIsActionMenuOpen(false)}
            />
          </div>
        ) : null}
      </span>
    </article>
  )
}
